/**
 * TaskRow - Task row using UniversalRow pattern
 *
 * Uses the universal slot system while maintaining task-specific features:
 * - Drag & drop for reordering and nesting
 * - Hierarchical completion (children first)
 * - Expand/collapse for nested tasks
 * - Timer integration
 * - Inline date editing
 * - Checklist progress badge
 *
 * See ROW_PATTERNS.md for the slot pattern documentation.
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  GripVertical,
  ChevronRight,
  ChevronDown,
  Circle,
  CheckCircle2,
  Play,
  Square,
  Clock,
  CalendarDays,
  ListChecks,
  AlertCircle,
} from 'lucide-react';
import {
  UniversalRow,
  FastActions,
  RowBadge,
  MetaSeparator,
  type FastActionType,
  type StatusLevel,
} from '../common/base';
import { STANDARD_FAST_ACTIONS } from '../../types/row.types';
import { InlineEdit } from './InlineEdit';
import type { Task, WidgetId } from '@business-assistant/shared/domain';
import { useActiveTimer, useStartTimer, useStopTimer } from '@business-assistant/shared';
import { LinkManager } from '../../services/actions/LinkManager';

// =============================================================================
// TYPES
// =============================================================================

export interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

export interface TaskRowProps {
  entity: Task;
  depth?: number;
  hasChildren?: boolean;
  expanded?: boolean;
  allChildrenDone?: boolean;
  checklist?: ChecklistItem[];
  onToggleDone?: (taskId: string) => void;
  onToggleExpand?: (taskId: string) => void;
  onRename?: (taskId: string, title: string) => void;
  onUpdateDates?: (taskId: string, dates: { startAt?: string; dueAt?: string }) => void;
  onOpenWidget?: (taskId: string, widgetId: WidgetId) => void;
  onTaskClick?: (taskId: string) => void;
  /** Also accept onTitleClick for compatibility with renderEntityRow */
  onTitleClick?: (entity: Task) => void;
  onDragStart?: (e: React.DragEvent, taskId: string) => void;
  onDragOver?: (e: React.DragEvent, taskId: string, position: 'before' | 'after' | 'child') => void;
  onDrop?: (e: React.DragEvent, taskId: string, position: 'before' | 'after' | 'child') => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function TaskRow({
  entity: task,
  depth = 0,
  hasChildren = false,
  expanded = false,
  allChildrenDone = true,
  checklist = [],
  onToggleDone,
  onToggleExpand,
  onRename,
  onUpdateDates,
  onOpenWidget,
  onTaskClick,
  onTitleClick,
  onDragStart,
  onDragOver,
  onDrop,
}: TaskRowProps) {
  const [editingDate, setEditingDate] = useState<'start' | 'due' | null>(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | 'child' | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  // Timer state
  const { data: activeTimer } = useActiveTimer();
  const startTimerMutation = useStartTimer();
  const stopTimerMutation = useStopTimer();
  const isTimerActive = activeTimer?.taskId === task.id;

  const startTimer = (taskId: string, projectId: string) => {
    startTimerMutation.mutate({ taskId, projectId });
  };

  const stopTimer = () => {
    stopTimerMutation.mutate();
  };

  // Elapsed time display
  const [elapsed, setElapsed] = useState('00:00');
  useEffect(() => {
    if (!isTimerActive || !activeTimer) return;

    const updateElapsed = () => {
      const start = new Date(activeTimer.startedAt).getTime();
      const now = Date.now();
      const totalSeconds = Math.floor((now - start) / 1000);
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      setElapsed(`${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [isTimerActive, activeTimer]);

  // Linked counts for fast actions
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(task.id);
  }, [task.id]);

  // Computed values
  const indentPx = depth * 20;
  const canComplete = !hasChildren || allChildrenDone;

  const isOverdue = useMemo(() => {
    if (!task.dueAt || task.done) return false;
    return new Date(task.dueAt) < new Date();
  }, [task.dueAt, task.done]);

  const isDueToday = useMemo(() => {
    if (!task.dueAt || task.done) return false;
    const due = new Date(task.dueAt).toDateString();
    const today = new Date().toDateString();
    return due === today;
  }, [task.dueAt, task.done]);

  // Checklist stats
  const checklistTotal = checklist.length;
  const checklistDone = checklist.filter(item => item.done).length;
  const hasChecklist = checklistTotal > 0;
  const isChecklistComplete = hasChecklist && checklistDone === checklistTotal;

  // === SLOT: Status ===
  const status: StatusLevel | undefined = useMemo(() => {
    if (task.done) return 'success';
    if (isOverdue) return 'danger';
    if (isDueToday) return 'warning';
    return undefined;
  }, [task.done, isOverdue, isDueToday]);

  // === SLOT: Left (drag handle + expand + checkbox) ===
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', paddingLeft: `${indentPx}px` }}>
      {/* Drag handle */}
      <span
        data-drag-handle
        style={{ cursor: 'grab', color: '#bbb', display: 'flex', padding: '2px' }}
      >
        <GripVertical size={14} />
      </span>

      {/* Expand/collapse */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          hasChildren && onToggleExpand?.(task.id);
        }}
        style={{
          background: 'none',
          border: 'none',
          padding: '2px',
          cursor: hasChildren && onToggleExpand ? 'pointer' : 'default',
          color: '#666',
          display: 'flex',
          opacity: hasChildren ? 1 : 0.3,
        }}
        disabled={!hasChildren || !onToggleExpand}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>

      {/* Checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          canComplete && onToggleDone?.(task.id);
        }}
        style={{
          background: 'none',
          border: 'none',
          padding: '2px',
          cursor: canComplete && onToggleDone ? 'pointer' : 'not-allowed',
          color: task.done ? '#22c55e' : canComplete ? '#bbb' : '#e5e5e5',
          display: 'flex',
        }}
        disabled={!canComplete || !onToggleDone}
        title={canComplete ? (task.done ? 'Mark incomplete' : 'Mark complete') : 'Complete subtasks first'}
      >
        {task.done ? <CheckCircle2 size={16} /> : <Circle size={16} />}
      </button>

      {/* Avatars */}
      <AvatarsStack assignees={task.assignees} />
    </div>
  );

  // === SLOT: Meta (dates, checklist, priority) ===
  const meta = useMemo(() => {
    const parts: React.ReactNode[] = [];

    // Due date chip
    if (task.dueAt && !editingDate) {
      parts.push(
        <DateChip
          key="due"
          icon={<Clock size={10} />}
          date={task.dueAt}
          overdue={isOverdue}
          onClick={() => setEditingDate('due')}
        />
      );
    }

    // Start date chip
    if (task.startAt && !editingDate) {
      parts.push(
        <DateChip
          key="start"
          icon={<CalendarDays size={10} />}
          date={task.startAt}
          onClick={() => setEditingDate('start')}
        />
      );
    }

    // Inline date editor
    if (editingDate) {
      parts.push(
        <input
          key="editor"
          type="date"
          defaultValue={editingDate === 'start' ? task.startAt?.split('T')[0] : task.dueAt?.split('T')[0]}
          autoFocus
          onClick={(e) => e.stopPropagation()}
          onBlur={(e) => {
            onUpdateDates?.(task.id, editingDate === 'start' ? { startAt: e.target.value || undefined } : { dueAt: e.target.value || undefined });
            setEditingDate(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onUpdateDates?.(task.id, editingDate === 'start' ? { startAt: e.currentTarget.value || undefined } : { dueAt: e.currentTarget.value || undefined });
              setEditingDate(null);
            }
            if (e.key === 'Escape') setEditingDate(null);
          }}
          style={{ fontSize: '11px', padding: '2px 4px', border: '1px solid #3b82f6', borderRadius: '3px' }}
        />
      );
    }

    // Checklist badge
    if (hasChecklist) {
      parts.push(
        <button
          key="checklist"
          onClick={(e) => {
            e.stopPropagation();
            onTaskClick?.(task.id);
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            padding: '1px 6px',
            background: isChecklistComplete ? '#dcfce7' : '#e0f2fe',
            borderRadius: '3px',
            fontSize: '11px',
            color: isChecklistComplete ? '#16a34a' : '#0369a1',
            fontWeight: 500,
            cursor: 'pointer',
            border: 'none',
          }}
          title={`Checklist: ${checklistDone}/${checklistTotal}`}
        >
          <ListChecks size={10} />
          {checklistDone}/{checklistTotal}
        </button>
      );
    }

    // Priority badge (if set)
    if (task.priority && task.priority !== 'none') {
      const priorityConfig = {
        low: { color: '#6b7280', label: 'Low' },
        medium: { color: '#f59e0b', label: 'Medium' },
        high: { color: '#ef4444', label: 'High' },
        urgent: { color: '#dc2626', label: '🔥 Urgent' },
      };
      const config = priorityConfig[task.priority as keyof typeof priorityConfig];
      if (config) {
        parts.push(
          <RowBadge key="priority" text={config.label} color={config.color} />
        );
      }
    }

    return parts.length > 0 ? <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>{parts}</div> : null;
  }, [task, editingDate, isOverdue, hasChecklist, checklistDone, checklistTotal, isChecklistComplete, onTaskClick, onUpdateDates]);

  // === SLOT: Right (timer + fast actions) ===
  const rightSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {/* Timer button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          isTimerActive ? stopTimer() : startTimer(task.id, task.projectId);
        }}
        style={{
          background: isTimerActive ? '#dcfce7' : 'none',
          border: 'none',
          padding: '4px 8px',
          cursor: 'pointer',
          color: isTimerActive ? '#16a34a' : '#aaa',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: isTimerActive ? 600 : 400,
        }}
        title={isTimerActive ? `Stop (${elapsed})` : 'Avvia timer'}
      >
        {isTimerActive ? <Square size={12} /> : <Play size={12} />}
        {isTimerActive && elapsed}
      </button>

      {/* Fast actions - standard actions across all rows */}
      <FastActions
        actions={STANDARD_FAST_ACTIONS as FastActionType[]}
        counts={linkedCounts as Partial<Record<FastActionType, number>>}
        onAction={(action) => {
          const widgetMap: Record<string, WidgetId> = {
            task: 'new-task',
            event: 'new-event',
            email: 'new-email',
            call: 'new-call',
            message: 'new-message',
            reminder: 'new-reminder',
            note: 'new-note',
          };
          onOpenWidget?.(task.id, widgetMap[action] || 'new-task');
        }}
      />
    </div>
  );

  // Drag handlers
  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
    onDragStart?.(e, task.id);
  }, [task.id, onDragStart]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!rowRef.current) return;
    const rect = rowRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;

    let position: 'before' | 'after' | 'child';
    if (y < height * 0.25) position = 'before';
    else if (y > height * 0.75) position = 'after';
    else position = 'child';

    setDropPosition(position);
    onDragOver?.(e, task.id, position);
  }, [task.id, onDragOver]);

  const handleDragLeave = useCallback(() => setDropPosition(null), []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropPosition) onDrop?.(e, task.id, dropPosition);
    setDropPosition(null);
  }, [task.id, dropPosition, onDrop]);

  // Custom title with inline edit (only editable if onRename is provided)
  const titleElement = onRename ? (
    <InlineEdit
      value={task.title}
      onCommit={(newTitle) => onRename(task.id, newTitle)}
      style={task.done ? { textDecoration: 'line-through', color: '#999' } : undefined}
    />
  ) : null;

  return (
    <div
      ref={rowRef}
      data-task-row
      data-task-id={task.id}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ position: 'relative' }}
    >
      {/* Drop indicators */}
      {dropPosition === 'before' && (
        <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '2px', background: '#3b82f6', pointerEvents: 'none' }} />
      )}
      {dropPosition === 'after' && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '2px', background: '#3b82f6', pointerEvents: 'none' }} />
      )}
      {dropPosition === 'child' && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(59,130,246,0.1)', pointerEvents: 'none' }} />
      )}

      {/* Universal Row */}
      <UniversalRow
        id={task.id}
        title={task.title}
        leftSlot={leftSlot}
        meta={meta}
        status={status}
        rightSlot={rightSlot}
        done={task.done}
        onTitleClick={() => {
          // Support both onTaskClick and onTitleClick
          if (onTaskClick) {
            onTaskClick(task.id);
          } else if (onTitleClick) {
            onTitleClick(task);
          }
        }}
      />
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function AvatarsStack({ assignees }: { assignees: string[] }) {
  if (assignees.length === 0) {
    return (
      <span style={{
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: '#e8e8e8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '9px',
        color: '#aaa',
        marginRight: '6px',
      }}>
        —
      </span>
    );
  }

  const visible = assignees.slice(0, 3);
  const extra = assignees.length - 3;

  return (
    <div style={{ display: 'flex', marginRight: '6px' }}>
      {visible.map((userId, i) => (
        <div
          key={userId}
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: '1.5px solid #fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '9px',
            fontWeight: 600,
            color: '#fff',
            background: `hsl(${(userId.charCodeAt(4) * 37) % 360}, 55%, 55%)`,
            marginLeft: i > 0 ? '-6px' : '0',
            zIndex: 3 - i,
          }}
          title={userId}
        >
          {userId.slice(-2).toUpperCase()}
        </div>
      ))}
      {extra > 0 && (
        <div style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          border: '1.5px solid #fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '9px',
          fontWeight: 600,
          color: '#fff',
          background: '#999',
          marginLeft: '-6px',
        }}>
          +{extra}
        </div>
      )}
    </div>
  );
}

function DateChip({
  icon,
  date,
  overdue = false,
  onClick,
}: {
  icon: React.ReactNode;
  date: string;
  overdue?: boolean;
  onClick: () => void;
}) {
  const formatted = new Date(date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        padding: '1px 6px',
        background: overdue ? '#fef2f2' : '#f0f0f0',
        borderRadius: '3px',
        fontSize: '11px',
        color: overdue ? '#dc2626' : '#666',
        cursor: 'pointer',
        border: 'none',
      }}
    >
      {icon}
      {formatted}
    </button>
  );
}
